import { useCallback, useEffect, useRef, useState } from 'react';

import {
  DISCOVERY_GUIDE_MIN_THUMB_WIDTH_PERCENT,
  DISCOVERY_GUIDE_SCROLL_STEP_RATIO,
} from '../constants';

const useDiscoveryGuideScrollbar = (cardCount: number) => {
  // DOM refs for the horizontal scroll rail and detached scrollbar thumb.
  const filtersRowRef = useRef<HTMLDivElement | null>(null);
  const scrollbarThumbRef = useRef<HTMLDivElement | null>(null);

  // requestAnimationFrame id so we only do one thumb update per frame.
  const scrollAnimationFrameIdRef = useRef<number | null>(null);
  const hasThumbSyncedRef = useRef(false);

  // Cached rail metrics reused across scroll handlers.
  const railMetricsRef = useRef({
    maxScrollLeftPx: 0,
  });

  const [hasOverflow, setHasOverflow] = useState(false);
  const [thumbWidthPercent, setThumbWidthPercent] = useState(100);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isScrollbarThumbReady, setIsScrollbarThumbReady] = useState(false);

  const updateScrollButtonState = useCallback((scrollLeftPx: number) => {
    const { maxScrollLeftPx } = railMetricsRef.current;
    // Treat sub-pixel offsets as boundaries to avoid nav button flicker.
    const scrollEpsilon = 1;

    setCanScrollLeft(scrollLeftPx > scrollEpsilon);
    setCanScrollRight(scrollLeftPx < maxScrollLeftPx - scrollEpsilon);
  }, []);

  const updateScrollbarThumbPosition = useCallback(
    (scrollLeftPx: number) => {
      // Keep nav button state accurate even before the thumb DOM node mounts.
      updateScrollButtonState(scrollLeftPx);

      const scrollbarThumbElement = scrollbarThumbRef.current;

      if (!scrollbarThumbElement) {
        return;
      }

      const { maxScrollLeftPx } = railMetricsRef.current;
      const scrollbarTrackElement = scrollbarThumbElement.parentElement;

      if (!scrollbarTrackElement) {
        return;
      }

      const maxThumbOffsetPx = Math.max(
        scrollbarTrackElement.clientWidth - scrollbarThumbElement.offsetWidth,
        0
      );

      const thumbLeftPx =
        maxScrollLeftPx > 0
          ? Math.min(
              (scrollLeftPx / maxScrollLeftPx) * maxThumbOffsetPx,
              maxThumbOffsetPx
            )
          : 0;

      scrollbarThumbElement.style.left = `${thumbLeftPx}px`;

      if (!hasThumbSyncedRef.current) {
        hasThumbSyncedRef.current = true;
        setIsScrollbarThumbReady(true);
      }
    },
    [updateScrollButtonState]
  );

  const syncScrollbarFromCurrentLayout = useCallback(() => {
    const scrollRailElement = filtersRowRef.current;

    if (!scrollRailElement) {
      return;
    }

    // Overflow + thumb sizing are derived from visible width vs total scroll width.
    const nextHasOverflow =
      scrollRailElement.scrollWidth > scrollRailElement.clientWidth;
    const nextThumbWidthRatio = scrollRailElement.scrollWidth
      ? scrollRailElement.clientWidth / scrollRailElement.scrollWidth
      : 0;
    const nextThumbWidthPercent = Math.min(
      Math.max(
        nextThumbWidthRatio * 100,
        DISCOVERY_GUIDE_MIN_THUMB_WIDTH_PERCENT
      ),
      100
    );

    setHasOverflow(nextHasOverflow);
    setThumbWidthPercent(nextThumbWidthPercent);

    if (!nextHasOverflow && hasThumbSyncedRef.current) {
      hasThumbSyncedRef.current = false;
      setIsScrollbarThumbReady(false);
    }

    railMetricsRef.current = {
      // Max rail travel in pixels.
      maxScrollLeftPx: Math.max(
        scrollRailElement.scrollWidth - scrollRailElement.clientWidth,
        0
      ),
    };

    updateScrollbarThumbPosition(scrollRailElement.scrollLeft);
  }, [updateScrollbarThumbPosition]);

  const handleRailScroll = useCallback(() => {
    const scrollRailElement = filtersRowRef.current;

    if (!scrollRailElement || scrollAnimationFrameIdRef.current) {
      return;
    }

    // Use rAF to keep thumb movement smooth while avoiding React state churn.
    scrollAnimationFrameIdRef.current = window.requestAnimationFrame(() => {
      scrollAnimationFrameIdRef.current = null;
      updateScrollbarThumbPosition(scrollRailElement.scrollLeft);
    });
  }, [updateScrollbarThumbPosition]);

  const scrollRailByStep = useCallback((direction: 'left' | 'right') => {
    const scrollRailElement = filtersRowRef.current;

    if (!scrollRailElement) {
      return;
    }

    const scrollStepPx =
      scrollRailElement.clientWidth * DISCOVERY_GUIDE_SCROLL_STEP_RATIO;
    const directionMultiplier = direction === 'right' ? 1 : -1;

    scrollRailElement.scrollBy({
      left: scrollStepPx * directionMultiplier,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    syncScrollbarFromCurrentLayout();
    window.addEventListener('resize', syncScrollbarFromCurrentLayout);

    return () => {
      if (scrollAnimationFrameIdRef.current) {
        window.cancelAnimationFrame(scrollAnimationFrameIdRef.current);
        scrollAnimationFrameIdRef.current = null;
      }
      window.removeEventListener('resize', syncScrollbarFromCurrentLayout);
    };
  }, [cardCount, syncScrollbarFromCurrentLayout]);

  useEffect(() => {
    const filtersRowElement = filtersRowRef.current;

    if (!hasOverflow || !filtersRowElement) {
      return;
    }

    // hasOverflow flips layout from centered to flex-start, so we remeasure
    // after paint to avoid using stale geometry for thumb size/position.
    const rafId = window.requestAnimationFrame(() => {
      syncScrollbarFromCurrentLayout();
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [hasOverflow, syncScrollbarFromCurrentLayout]);

  return {
    filtersRowRef,
    scrollbarThumbRef,
    hasOverflow,
    thumbWidthPercent,
    handleRailScroll,
    canScrollLeft,
    canScrollRight,
    isScrollbarThumbReady,
    scrollRailByStep,
  };
};

export default useDiscoveryGuideScrollbar;
